/**
 * Viewport Text Editor — sandbox wiring.
 *
 * The controller itself (in-scene caret/selection editing + the standing
 * Text bounds box) is the shared `ViewportTextEditor` in
 * `trace-viewer/editor`. This module only supplies the sandbox-specific
 * pieces: viewer discovery, per-scene lifecycle (mirroring
 * viewport-context-menu), and the Inspector gizmo suppression hook.
 */

import type { Scene } from '@babylonjs/core/scene';
import { createViewportTextEditor } from 'trace-viewer/editor';
import type { ViewportTextEditor } from 'trace-viewer/editor';
import { suppressTransformGizmos } from './gizmo-keyboard-service';

let editor: ViewportTextEditor | null = null;
let attachedScene: Scene | null = null;
let restoreGizmos: (() => void) | null = null;

/**
 * Attach the inline text editor to the current viewer scene.
 * Safe to call repeatedly — re-attaches if the scene changed.
 */
export function setupViewportTextEditor(): void {
    const viewer = document.querySelector('trace-viewer');
    const scene = viewer?.scene ?? null;
    if (!viewer || !scene) return;
    if (scene === attachedScene) return;

    teardownViewportTextEditor();
    attachedScene = scene;
    void createViewportTextEditor(viewer, {
        // Transform gizmo handles fight the caret for clicks (and the
        // planar disc sits right on the text origin) — hide them all for
        // the duration of an edit session, restore afterwards.
        onSessionActive: (active) => {
            restoreGizmos?.();
            restoreGizmos = active ? suppressTransformGizmos() : null;
        },
    }).then((created) => {
        // The factory is async (lazy-loaded controller); if the scene was
        // torn down or re-setup while loading, discard this instance.
        if (attachedScene !== scene || viewer.scene !== scene) {
            created.dispose();
            return;
        }
        editor = created;
        // Debug/automation handle (AI gym, e2e drivers).
        Object.assign(window, { __viewportTextEditor: created });
    });
}

/** Dispose the inline text editor and its observers. */
export function teardownViewportTextEditor(): void {
    editor?.dispose();
    editor = null;
    attachedScene = null;
    restoreGizmos?.();
    restoreGizmos = null;
}
